import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"

import { Flex } from "antd"

import Private from "../../../../Components/Private"
import { formatUsageMoney } from "./chartOptions"

const UsageOverviewTooltip = forwardRef(function UsageOverviewTooltip(_, ref) {
  const [payload, setPayload] = useState(null)
  useImperativeHandle(ref, () => ({ update: setPayload }), [])
  return createPortal(<TooltipContent {...payload} />, document.body)
})

const TooltipContent = ({ x, y, date, visible = false, data }) => {
  const tooltipRef = useRef(null)
  const { adjustedX, adjustedY } = useMemo(() => {
    const rect = tooltipRef.current?.getBoundingClientRect()
    const width = rect?.width || 0
    let adjustedX = x
    const xTranslate = -(width / 2)
    const xTranslated = x + xTranslate
    if (xTranslated + width > window.innerWidth) {
      adjustedX = window.innerWidth + xTranslate
    }
    if (xTranslated < 0) adjustedX = -xTranslate
    return { adjustedX, adjustedY: y }
  }, [x, y])

  const groups = useMemo(() => {
    if (!Array.isArray(data)) return { series: [] }
    const map = new Map()
    data.forEach((point) => {
      const type = point?.datasetType
      if (!type) return
      const isPrev = type.endsWith("_previous")
      const key = isPrev ? type.slice(0, -"_previous".length) : type
      if (!map.has(key)) map.set(key, { key, main: null, prev: null })
      const entry = map.get(key)
      if (isPrev) entry.prev = point
      else entry.main = point
    })
    return { series: Array.from(map.values()) }
  }, [data])

  const style = { top: adjustedY, left: adjustedX, opacity: visible ? 1 : 0 }
  const prevDate = groups.series.find((g) => g.prev)?.prev?.comparisonDate
  const hasMain = groups.series.some((g) => g.main)
  const hasPrev = groups.series.some((g) => g.prev)

  return (
    <div
      id="billing-usage-overview-tooltip"
      className="billing-usage-overview-tooltip"
      style={style}
      ref={tooltipRef}
    >
      <Flex vertical gap={8}>
        {hasMain && (
          <Flex vertical gap={2} className="tooltip-group">
            <span className="tooltip-group-title">{date}</span>
            {groups.series.map(
              (group) =>
                group.main && <Row key={group.key} point={group.main} label={group.main.label} />
            )}
          </Flex>
        )}
        {hasPrev && prevDate && (
          <Flex vertical gap={2} className="tooltip-group">
            <span className="tooltip-group-title">{prevDate}</span>
            {groups.series.map(
              (group) =>
                group.prev && (
                  <Row
                    key={group.key}
                    point={group.prev}
                    label={group.main?.label || group.prev.label}
                  />
                )
            )}
          </Flex>
        )}
      </Flex>
    </div>
  )
}

const Row = ({ point, label }) => (
  <Flex justify="space-between" align="center" gap={12} className="tooltip-row">
    <Flex gap={8} align="center">
      <span
        className={`line-dataset-indicator${point?.dashed ? " dashed" : ""}`}
        style={
          point?.dashed
            ? {
                backgroundImage: `repeating-linear-gradient(to right, ${point?.backgroundColor} 0 3px, transparent 3px 6px)`
              }
            : { background: point?.backgroundColor }
        }
      />
      <span>{label}</span>
    </Flex>
    <span className="tooltip-value">
      <Private>{formatUsageMoney(point?.value)}</Private>
    </span>
  </Flex>
)

export default UsageOverviewTooltip
